/**
 * Testcase API Functions
 *
 * HTTP functions for fetching testcase data.
 * Also populates the query cache so testcaseMolecule.get.data(id) works.
 */

import {axios, getAgentaApiUrl} from "@agenta/shared/api"
import {getDefaultStore} from "jotai/vanilla"
import {queryClientAtom} from "jotai-tanstack-query"

// Import from the pure zodSchema source rather than the shared barrel. The
// shared barrel transitively re-exports paginated/table helpers that depend on
// agenta-ui (CSS modules), which breaks Node-side execution (scripts, tests,
// ETL adapters). The api layer must stay Node-safe.
import {safeParseWithLogging} from "../../shared/utils/zodSchema"
import {testcasesResponseSchema, type Testcase, type TestcasesResponse} from "../core"
import type {
    TestcaseDetailParams,
    TestcaseListParams,
    TestcaseBatchParams,
    TestcasesPage,
} from "../core"

// ============================================================================
// SINGLE TESTCASE FETCH
// ============================================================================

/**
 * Fetch a single testcase by ID
 *
 * @example
 * ```typescript
 * const testcase = await fetchTestcase({
 *   projectId: 'proj-123',
 *   testcaseId: 'tc-456'
 * })
 * ```
 */
export async function fetchTestcase(params: TestcaseDetailParams): Promise<Testcase | null> {
    const {projectId, testcaseId} = params

    try {
        const response = await axios.post(
            `${getAgentaApiUrl()}/testcases/query`,
            {testcase_ids: [testcaseId]},
            {params: {project_id: projectId}},
        )

        const validatedResponse = safeParseWithLogging(
            testcasesResponseSchema,
            response.data,
            "[fetchTestcase]",
        )
        if (!validatedResponse) return null

        return validatedResponse.testcases[0] || null
    } catch (error) {
        console.error("[fetchTestcase] Failed to fetch testcase:", error)
        return null
    }
}

// ============================================================================
// BATCH TESTCASE FETCH
// ============================================================================

/**
 * Fetch multiple testcases by IDs
 *
 * @example
 * ```typescript
 * const testcases = await fetchTestcasesBatch({
 *   projectId: 'proj-123',
 *   testcaseIds: ['tc-1', 'tc-2', 'tc-3']
 * })
 * ```
 */
export async function fetchTestcasesBatch(
    params: TestcaseBatchParams,
): Promise<Map<string, Testcase>> {
    const {projectId, testcaseIds} = params
    const results = new Map<string, Testcase>()

    if (!projectId || testcaseIds.length === 0) {
        return results
    }

    try {
        const response = await axios.post(
            `${getAgentaApiUrl()}/testcases/query`,
            {testcase_ids: testcaseIds},
            {params: {project_id: projectId}},
        )

        const validatedResponse = safeParseWithLogging(
            testcasesResponseSchema,
            response.data,
            "[fetchTestcasesBatch]",
        )
        if (validatedResponse) {
            for (const testcase of validatedResponse.testcases) {
                results.set(testcase.id, testcase)
            }
            // Populate the TanStack cache so subsequent reads via
            // `testcaseMolecule.get.data(id)` and `prefetchTestcasesByIds`
            // hit cache. Matches the cache-write behaviour of
            // `fetchTestcasesPage` — the two batch fetchers now consistent.
            try {
                const store = getDefaultStore()
                const queryClient = store.get(queryClientAtom)
                for (const tc of validatedResponse.testcases) {
                    queryClient.setQueryData(["testcase", projectId, tc.id], tc)
                }
            } catch {
                // Silently ignore if query client not available (SSR, scripts).
            }
        }
    } catch (error) {
        console.error("[fetchTestcasesBatch] Failed to fetch testcases:", error)
    }

    return results
}

// ============================================================================
// PAGINATED TESTCASE FETCH
// ============================================================================

/**
 * Default page size for pagination
 */
export const PAGE_SIZE = 50

/**
 * Fetch a page of testcases for a revision
 *
 * @example
 * ```typescript
 * const page = await fetchTestcasesPage({
 *   projectId: 'proj-123',
 *   revisionId: 'rev-456',
 *   cursor: null,
 *   limit: 50
 * })
 * ```
 */
export async function fetchTestcasesPage(params: TestcaseListParams): Promise<TestcasesPage> {
    const {projectId, revisionId, cursor = null, limit = PAGE_SIZE} = params

    if (!projectId || !revisionId) {
        return {
            testcases: [],
            count: 0,
            nextCursor: null,
            hasMore: false,
        }
    }

    try {
        const response = await axios.post(
            `${getAgentaApiUrl()}/testcases/query`,
            {
                testset_revision_ref: {id: revisionId},
                windowing: {
                    limit,
                    ...(cursor && {next: cursor}),
                },
            },
            {params: {project_id: projectId}},
        )

        const validated = safeParseWithLogging(
            testcasesResponseSchema,
            response.data,
            "[fetchTestcasesPage]",
        )
        if (!validated) {
            return {
                testcases: [],
                count: 0,
                nextCursor: null,
                hasMore: false,
            }
        }

        // Store testcases in nested format (not flattened)
        // Cell values are accessed via testcase.data[columnKey] through testcaseCellAtomFamily
        const testcases = validated.testcases

        // Populate the query cache so testcaseMolecule.get.data(id) works
        // This ensures the entity layer has access to testcases fetched by the paginated store
        try {
            const store = getDefaultStore()
            const queryClient = store.get(queryClientAtom)
            for (const tc of testcases) {
                queryClient.setQueryData(["testcase", projectId, tc.id], tc)
            }
        } catch {
            // Silently ignore if query client not available (e.g., during SSR)
        }

        return {
            testcases,
            count: validated.count,
            nextCursor: validated.windowing?.next || null,
            hasMore: Boolean(validated.windowing?.next),
        }
    } catch (error) {
        console.error("[fetchTestcasesPage] Failed to fetch testcases page:", error)
        return {
            testcases: [],
            count: 0,
            nextCursor: null,
            hasMore: false,
        }
    }
}

// ============================================================================
// RAW API RESPONSE FETCH
// ============================================================================

/**
 * Fetch raw testcases response (for advanced use cases)
 */
export async function fetchTestcasesRaw(
    projectId: string,
    query: {
        testcase_ids?: string[]
        testset_id?: string
        testset_revision_ref?: {
            id?: string
        }
        windowing?: {
            newest?: string
            oldest?: string
            next?: string
            limit?: number
            order?: "ascending" | "descending"
        }
    },
): Promise<TestcasesResponse | null> {
    try {
        const response = await axios.post(`${getAgentaApiUrl()}/testcases/query`, query, {
            params: {project_id: projectId},
        })

        return safeParseWithLogging(testcasesResponseSchema, response.data, "[fetchTestcasesRaw]")
    } catch (error) {
        console.error("[fetchTestcasesRaw] Failed to fetch testcases:", error)
        return null
    }
}
